import React, { useEffect, useMemo, useState } from "react";
import { Shell, Section } from "@/components/loomi/Layout";
import { ADMIN_NAV, ProjectsTable } from "./AdminDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Search, Trash2, Filter } from "lucide-react";

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);

  // filters
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");

  // bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (clientId) params.client_id = clientId;
    const [p, c] = await Promise.all([
      api.get("/projects", { params }),
      api.get("/clients"),
    ]);
    setProjects(p.data);
    setClients(c.data);
  };

  // initial + reload when filters change (debounced for search)
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, clientId]);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.name])), [clients]);

  const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAll = (checked) => setSelectedIds(checked ? projects.map((p) => p.id) : []);

  const clearFilters = () => { setSearch(""); setClientId(""); };
  const anyFilterActive = !!(search.trim() || clientId);

  const doBulkDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/projects/bulk-delete", { ids: selectedIds });
      toast.success(`Deleted ${data.deleted} project${data.deleted === 1 ? "" : "s"}`);
      setSelectedIds([]);
      setConfirmingDelete(false);
      await load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    } finally { setDeleting(false); }
  };

  return (
    <Shell title="Projects" portalLabel="Admin" nav={ADMIN_NAV}>
      <Section
        title="All projects"
        action={
          <button onClick={() => setOpen(true)} data-testid="admin-new-project-btn" className="bg-indigo-500 hover:bg-indigo-400 text-white text-xs font-medium px-3 py-2 rounded-md inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New project
          </button>
        }
        testId="section-all-projects"
      >
        {/* Filter bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-5" data-testid="projects-filter-bar">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-mute pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search project name…"
              data-testid="projects-search-input"
              className="w-full bg-black/40 border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
          </div>
          <div className="relative">
            <Filter className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-mute pointer-events-none" />
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="projects-filter-client"
              className="bg-black/40 border border-white/10 rounded-md pl-9 pr-8 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-[220px]"
            >
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {anyFilterActive && (
            <button
              onClick={clearFilters}
              data-testid="projects-filter-clear"
              className="text-xs text-mute hover:text-white border border-white/10 hover:bg-white/5 rounded-md px-3 py-2 inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <div className="text-xs text-mute flex items-center px-2" data-testid="projects-count">
            {projects.length} project{projects.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* Bulk-action toolbar (only visible when items selected) */}
        {selectedIds.length > 0 && (
          <div className="mb-4 rounded-lg border border-indigo-400/30 bg-indigo-500/10 px-4 py-2.5 flex items-center justify-between" data-testid="bulk-action-bar">
            <div className="text-sm">
              <span className="font-semibold" data-testid="bulk-selected-count">{selectedIds.length}</span> selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedIds([])}
                data-testid="bulk-clear-selection"
                className="text-xs text-mute hover:text-white px-3 py-1.5 rounded-md hover:bg-white/5"
              >
                Clear
              </button>
              <button
                onClick={() => setConfirmingDelete(true)}
                data-testid="bulk-delete-btn"
                className="text-xs bg-rose-500 hover:bg-rose-400 text-white font-medium px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete {selectedIds.length}
              </button>
            </div>
          </div>
        )}

        <ProjectsTable
          projects={projects}
          basePath="/admin/projects"
          selectable
          selectedIds={selectedIds}
          onToggle={toggle}
          onToggleAll={toggleAll}
          clientMap={clientMap}
        />
      </Section>

      {open && <NewProjectModal clients={clients} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {confirmingDelete && (
        <ConfirmDeleteModal
          count={selectedIds.length}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={doBulkDelete}
          busy={deleting}
        />
      )}
    </Shell>
  );
}

function ConfirmDeleteModal({ count, onCancel, onConfirm, busy }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onCancel} data-testid="confirm-delete-modal">
      <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl max-w-md w-full p-6">
        <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mb-4">
          <Trash2 className="w-5 h-5 text-rose-300" />
        </div>
        <h3 className="font-display font-extrabold text-xl mb-1">Delete {count} project{count === 1 ? "" : "s"}?</h3>
        <p className="text-sm text-mute leading-relaxed">
          This permanently removes the selected project{count === 1 ? "" : "s"} along with all their sessions, redirect logs, and URL records. This cannot be undone.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} data-testid="confirm-delete-cancel" className="px-4 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10">Cancel</button>
          <button onClick={onConfirm} disabled={busy} data-testid="confirm-delete-submit" className="px-4 py-2 rounded-md text-sm bg-rose-500 hover:bg-rose-400 disabled:opacity-60 font-medium">
            {busy ? "Deleting…" : `Delete ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ clients, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: "api", name: "", survey_url: "", client_id: clients[0]?.id || "",
    target_n: 100, cpi: 1.5, loi: 10, incidence_rate: 50, reward_points: 50, is_public: false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      onSaved();
    } catch (err) {
      toast.error("Failed", { description: formatApiError(err) });
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="new-project-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl w-full max-w-lg p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-white" data-testid="new-project-close"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-extrabold text-2xl mb-1">New project</h3>
        <p className="text-mute text-sm mb-4">Admin can create API or ad-hoc projects.</p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select value={form.type} onChange={set("type")} data-testid="new-project-type" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm">
              <option value="api">API</option>
              <option value="adhoc">Ad-hoc</option>
            </select>
          </Field>
          <Field label="Client">
            <select value={form.client_id} onChange={set("client_id")} data-testid="new-project-client" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Name" wide>
            <input value={form.name} onChange={set("name")} required data-testid="new-project-name" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Survey URL" wide>
            <input value={form.survey_url} onChange={set("survey_url")} required data-testid="new-project-survey-url" placeholder="https://survey.example.com/s/..." className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
          </Field>
          <Field label="Target N"><input type="number" value={form.target_n} onChange={set("target_n")} data-testid="new-project-target-n" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm" /></Field>
          <Field label="CPI ($)"><input type="number" step="0.01" value={form.cpi} onChange={set("cpi")} data-testid="new-project-cpi" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm" /></Field>
          <Field label="LOI (min)"><input type="number" value={form.loi} onChange={set("loi")} data-testid="new-project-loi" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm" /></Field>
          <Field label="Incidence %"><input type="number" value={form.incidence_rate} onChange={set("incidence_rate")} data-testid="new-project-ir" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm" /></Field>
          <Field label="Reward pts"><input type="number" value={form.reward_points} onChange={set("reward_points")} data-testid="new-project-reward" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm" /></Field>
          {form.type === "adhoc" && (
            <Field label="Visibility">
              <select value={form.is_public ? "public" : "targeted"} onChange={(e) => setForm({ ...form, is_public: e.target.value === "public" })} data-testid="new-project-visibility" className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm">
                <option value="public">Public</option>
                <option value="targeted">Targeted</option>
              </select>
            </Field>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10" data-testid="new-project-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="new-project-submit" className="px-4 py-2 rounded-md text-sm bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60">{saving ? "Saving…" : "Create project"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">{label}</label>
      {children}
    </div>
  );
}
